
import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseUpstreamDatabasesFilteredByTableQuery,
  DatabaseUpstreamDatabasesFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseUpstreamDatabasesFilteredByTableQuery';
import {
  DatabaseUpstreamDatabasesQuery,
  DatabaseUpstreamDatabasesQueryVariables,
} from '../../../gen-src/queries/DatabaseUpstreamDatabasesQuery';
import { DatabasesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatabasesDisplayGridRowDataFragment';
import { DatabaseSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  DatabaseUpstreamDatabasesFilteredByTableQueryGql,
} from '../../queries/DatabaseUpstreamDatabasesFilteredByTableQueryGql';
import { DatabaseUpstreamDatabasesQueryGql } from '../../queries/DatabaseUpstreamDatabasesQueryGql';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';

type QueryVariables = DatabaseUpstreamDatabasesQueryVariables;
type QueryResults = DatabaseUpstreamDatabasesQuery;
type QueryResultListNode = DatabasesDisplayGridRowDataFragment;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = DatabaseUpstreamDatabasesFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseUpstreamDatabasesFilteredByTableQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].upstreamDatabasesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].upstreamDatabasesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.databases && resultData.databases[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.tables && resultData.tables[0];
}

function getRowDataForDatabase(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatabaseUpstreamDatabasesQueryGridProps =
  GridSortProps<DatabasesValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseUpstreamDatabasesQueryGrid(props: DatabaseUpstreamDatabasesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyUpstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatabasesLineageGridMessage();

  return props.filteredTableId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DatabaseUpstreamDatabasesFilteredByTableQueryGql}
      partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as DatabaseSortOrder, publishedConnectionsLineageEnabled }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseUpstreamDatabasesQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as DatabaseSortOrder, publishedConnectionsLineageEnabled }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatabase}
      >
        {({ rowStore }) =>
          <DatabasesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    );
}
